/**
 * TopBar — Sticky full-width header.
 *
 * Layout (LTR):
 *   [Logo + AppName]
 *   [ContextGridSwitcher]    (persona/role grid dropdown)
 *   [MegaMenuGrid]            (module launcher grid dropdown)
 *   [Global search]
 *   (flex spacer)
 *   [AI credits chip] [Plan chip]
 *   [Language] [Theme] [Messages] [Billing] [Notifications]
 *   [Mode toggle]
 *   [User menu]
 */

import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import {
  Bell, Moon, Sun, Globe, User, LogOut, Settings,
  ToggleLeft, ToggleRight,
  BrainCircuit, Sparkles, MessageSquare, CreditCard,
  Command, Keyboard, Search, Rss,
} from 'lucide-react'

import { useAuth } from '@/lib/auth/useAuth'
import { useCreditBalance } from '@/services/credits.service'
import { useTheme } from '@/components/providers/ThemeProvider'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { formatNumber } from '@/lib/jurisdiction/jurisdiction-config'
import { useNotifications } from '@/services/notification.service'
import { GlobalSearch } from '@/components/shared/global-search'
import { useCommandPalette } from '@/components/ui/command-palette'
import { openShortcutsHelp } from '@/components/ui/shortcuts-help'
import { useJourneyStore } from '@/stores/useJourneyStore'
import { ContextGridSwitcher } from './ContextGridSwitcher'
import { ContextDescription } from './ContextDescription'
import { CurrencySelector } from './CurrencySelector'
import { ThemeSwitcher } from './ThemeSwitcher'
import { MobileNavDrawer } from './MobileNavDrawer'
import { useCollabUnread } from '@/hooks/useCollabUnread'
import { useFeedUnread } from '@/services/social-feed.service'

export function TopBar() {
  const { t, i18n } = useTranslation()
  const { user, logout } = useAuth()
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()
  const { setOpen: setPaletteOpen } = useCommandPalette()
  const isMac =
    typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent)
  const activeContext = useJourneyStore((s) => s.activeContext)
  const mode = useJourneyStore((s) => s.mode)
  const toggleMode = useJourneyStore((s) => s.toggleMode)
  const isAgentMode = mode === 'pro'
  const chatUnread = useCollabUnread()
  const { data: feedUnread } = useFeedUnread()

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark')
  const toggleLanguage = () =>
    i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr')

  const getInitials = (name: string) =>
    name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)

  const { data: notificationsData } = useNotifications({
    page: 1,
    pageSize: 10,
    unreadOnly: true,
  })
  const unreadCount = notificationsData?.items?.filter((n: any) => !n.read).length || 0

  // Real-time AI-credit balance (plan + extra) — refetches every 15s + on focus.
  const { data: creditBalance } = useCreditBalance({ realtime: true })
  const aiCredits = {
    remaining: creditBalance?.ai?.total ?? 0,
    total:
      (creditBalance?.ai?.monthly_quota ?? 0) + (creditBalance?.ai?.purchased ?? 0) ||
      (creditBalance?.ai?.total ?? 0),
  }
  const planName = 'Pro'

  return (
    <header className="sticky top-0 z-40 shrink-0 border-b bg-[hsl(var(--topbar-bg))] text-[hsl(var(--topbar-fg))] border-[hsl(var(--topbar-border))] backdrop-blur">
      <div className="flex h-14 items-center gap-2 px-4">
        {/* Mobile navigation drawer (hamburger) — hidden on md+ */}
        <MobileNavDrawer />

        {/* Logo + AppName */}
        <Link to="/dashboard" className="flex items-center gap-2 shrink-0 mr-1">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-blue-400 shadow-md shadow-primary/20">
            <span className="text-white font-black text-[12px]">CP</span>
          </div>
          <div className="hidden md:flex flex-col leading-tight">
            <span className="text-[13px] font-extrabold text-[hsl(var(--topbar-fg))] tracking-tight">Client Portal</span>
            <span className="text-[9px] font-semibold text-[hsl(var(--topbar-fg))]/60 uppercase tracking-[0.18em]">SGRC</span>
          </div>
        </Link>

        <div className="h-6 w-px bg-[hsl(var(--topbar-fg))]/20 mx-1 shrink-0" />

        {/* Context grid switcher */}
        <ContextGridSwitcher align="start" />

        {/* Context description + feature shortcuts */}
        <ContextDescription />

        {/* Global search (hidden on small screens) */}
        <GlobalSearch className="hidden md:flex flex-1 max-w-sm mx-2" />

        {/* Command palette launcher — discoverable ⌘K affordance (desktop) */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                aria-label="Palette de commandes"
                className={cn(
                  'hidden md:flex items-center gap-2 h-8 rounded-full pl-2.5 pr-1.5 shrink-0',
                  'border border-[hsl(var(--topbar-fg))]/20 bg-[hsl(var(--topbar-fg))]/10 hover:bg-[hsl(var(--topbar-fg))]/20 transition-colors',
                  'text-[11px] font-medium text-[hsl(var(--topbar-fg))]/80',
                )}
              >
                <Search className="h-3.5 w-3.5" />
                <span className="hidden lg:inline">Commandes</span>
                <kbd className="flex items-center gap-0.5 rounded border border-[hsl(var(--topbar-fg))]/25 bg-[hsl(var(--topbar-fg))]/10 px-1 py-0.5 font-mono text-[10px] leading-none text-[hsl(var(--topbar-fg))]">
                  {isMac ? <Command className="h-2.5 w-2.5" /> : 'Ctrl'}K
                </kbd>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Palette de commandes — rechercher, naviguer, créer
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Mobile: compact palette launcher (search icon) */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setPaletteOpen(true)}
          aria-label="Palette de commandes"
          className="md:hidden h-8 w-8 rounded-full text-[hsl(var(--topbar-fg))] hover:bg-[hsl(var(--topbar-fg))]/15 hover:text-[hsl(var(--topbar-fg))]"
        >
          <Search className="h-4 w-4" />
        </Button>

        {/* Keyboard-shortcuts help (?) */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => openShortcutsHelp()}
                aria-label="Raccourcis clavier"
                className="hidden md:inline-flex h-8 w-8 rounded-full text-[hsl(var(--topbar-fg))] hover:bg-[hsl(var(--topbar-fg))]/15 hover:text-[hsl(var(--topbar-fg))]"
              >
                <Keyboard className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Raccourcis clavier — appuyez sur <kbd className="font-mono">?</kbd>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Spacer (only when search is hidden) */}
        <div className="flex-1 md:hidden" />

        {/* AI credits chip */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/account/credits"
                className={cn(
                  'hidden md:flex items-center gap-1.5 h-8 rounded-full px-2.5 shrink-0',
                  'border border-[hsl(var(--topbar-fg))]/20 bg-[hsl(var(--topbar-fg))]/10 hover:bg-[hsl(var(--topbar-fg))]/20 transition-colors',
                  'text-[11px] font-semibold text-[hsl(var(--topbar-fg))]',
                )}
                aria-label="Crédits IA"
              >
                <BrainCircuit className="h-3.5 w-3.5" />
                <span className="tabular-nums">
                  {formatNumber(aiCredits.remaining)}
                </span>
                <span className="text-[hsl(var(--topbar-fg))]/80">/{formatNumber(aiCredits.total)}</span>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="bottom">Crédits IA disponibles</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Plan chip */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/account/subscription"
                className={cn(
                  'hidden md:flex items-center gap-1.5 h-8 rounded-full px-2.5 shrink-0',
                  'border border-[hsl(var(--topbar-fg))]/20 bg-[hsl(var(--topbar-fg))]/10 hover:bg-[hsl(var(--topbar-fg))]/20 transition-colors',
                  'text-[11px] font-semibold text-[hsl(var(--topbar-fg))]',
                )}
                aria-label="Plan"
              >
                <Sparkles className="h-3.5 w-3.5" />
                <span>{planName}</span>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="bottom">Plan d'abonnement</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div className="h-5 w-px bg-[hsl(var(--topbar-fg))]/20 shrink-0 hidden md:block" />

        {/* Language */}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleLanguage}
          className="gap-1 text-xs hidden sm:flex h-8 rounded-full px-2 text-[hsl(var(--topbar-fg))] hover:bg-[hsl(var(--topbar-fg))]/15 hover:text-[hsl(var(--topbar-fg))]"
        >
          <Globe className="h-3.5 w-3.5" />
          <span className="hidden lg:inline">
            {i18n.language === 'fr' ? 'FR' : 'EN'}
          </span>
        </Button>

        {/* Currency */}
        <CurrencySelector />

        {/* Light/Dark quick toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full text-[hsl(var(--topbar-fg))] hover:bg-[hsl(var(--topbar-fg))]/15 hover:text-[hsl(var(--topbar-fg))]"
          onClick={toggleTheme}
          aria-label="Thème clair/sombre"
        >
          {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
        </Button>

        {/* Color theme swatch picker — hidden on phones (reachable via settings) */}
        <div className="hidden md:flex">
          <ThemeSwitcher />
        </div>

        {/* Social feed — global network feed (SOCIAL_LAYER_PLAN §A). Visible to all. */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/feed" className="inline-flex">
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-8 w-8 rounded-full text-[hsl(var(--topbar-fg))] hover:bg-[hsl(var(--topbar-fg))]/15 hover:text-[hsl(var(--topbar-fg))]"
                  aria-label="Fil d'actualité"
                >
                  <Rss className="h-3.5 w-3.5" />
                  {feedUnread?.hasNew && (
                    <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-[hsl(var(--topbar-bg))]" />
                  )}
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="bottom">Fil d'actualité</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Messages — hidden on phones (reachable via Chat nav in drawer) */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/chat" className="hidden md:inline-flex">
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-8 w-8 rounded-full text-[hsl(var(--topbar-fg))] hover:bg-[hsl(var(--topbar-fg))]/15 hover:text-[hsl(var(--topbar-fg))]"
                  aria-label="Messages"
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  {chatUnread > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -right-1 -top-1 h-4 w-4 rounded-full p-0 text-[10px] flex items-center justify-center"
                    >
                      {chatUnread > 9 ? '9+' : chatUnread}
                    </Badge>
                  )}
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="bottom">Messages & Chat IA</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Billing — hidden on phones (reachable via user menu → Facturation) */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/self-manage/finances" className="hidden md:inline-flex">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full text-[hsl(var(--topbar-fg))] hover:bg-[hsl(var(--topbar-fg))]/15 hover:text-[hsl(var(--topbar-fg))]"
                  aria-label="Facturation"
                >
                  <CreditCard className="h-3.5 w-3.5" />
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="bottom">Facturation</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Notifications */}
        <Link to="/settings/notifications">
          <Button
            variant="ghost"
            size="icon"
            className="relative h-8 w-8 rounded-full text-[hsl(var(--topbar-fg))] hover:bg-[hsl(var(--topbar-fg))]/15 hover:text-[hsl(var(--topbar-fg))]"
            aria-label="Notifications"
          >
            <Bell className="h-3.5 w-3.5" />
            {unreadCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -right-1 -top-1 h-4 w-4 rounded-full p-0 text-[10px] flex items-center justify-center"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </Badge>
            )}
          </Button>
        </Link>

        {/* Mode toggle — hidden on phones (mode is a desktop power-user control) */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleMode}
                className={cn(
                  'hidden md:flex h-8 gap-1.5 text-[11px] font-medium border-2 rounded-full transition-all shrink-0',
                  isAgentMode
                    ? 'border-[hsl(var(--topbar-fg))]/60 bg-[hsl(var(--topbar-fg))]/15 text-[hsl(var(--topbar-fg))]'
                    : 'border-[hsl(var(--topbar-fg))]/30 text-[hsl(var(--topbar-fg))]/80 hover:bg-[hsl(var(--topbar-fg))]/10',
                )}
              >
                {isAgentMode
                  ? <ToggleRight className="h-3.5 w-3.5" />
                  : <ToggleLeft className="h-3.5 w-3.5" />}
                <span className="hidden lg:inline">
                  {isAgentMode ? 'Mode Agent' : 'Mode Utilisateur'}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {isAgentMode ? 'Désactiver le mode PRO' : 'Activer le mode PRO'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div className="h-5 w-px bg-border/50 shrink-0 hidden md:block" />

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-8 gap-2 shrink-0 rounded-full pl-1 pr-2 text-[hsl(var(--topbar-fg))] hover:bg-[hsl(var(--topbar-fg))]/15 hover:text-[hsl(var(--topbar-fg))]"
            >
              <Avatar className="h-7 w-7 ring-2 ring-[hsl(var(--topbar-fg))]/25">
                <AvatarFallback className="bg-gradient-to-br from-primary to-blue-400 text-white text-[11px] font-bold">
                  {user?.name ? getInitials(user.name) : 'U'}
                </AvatarFallback>
              </Avatar>
              <span className="hidden lg:inline text-[12px] font-semibold text-[hsl(var(--topbar-fg))] max-w-[100px] truncate">
                {user?.name ?? 'Utilisateur'}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="font-semibold">{user?.name}</span>
                <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
                <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0 text-[9px] font-bold uppercase tracking-wider text-primary">
                  <Sparkles className="h-2.5 w-2.5" /> {planName}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings/profile')}>
              <User className="mr-2 h-4 w-4" />{t('common.profile')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/account/credits')}>
              <BrainCircuit className="mr-2 h-4 w-4" />Crédits IA
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/self-manage/finances')}>
              <CreditCard className="mr-2 h-4 w-4" />Facturation
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings/notifications')}>
              <Settings className="mr-2 h-4 w-4" />{t('navigation.settings')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openShortcutsHelp()}>
              <Keyboard className="mr-2 h-4 w-4" />Raccourcis clavier
              <kbd className="ml-auto rounded border bg-muted px-1 font-mono text-[10px]">?</kbd>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />{t('common.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
